import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import {
  canContinueWithNewSocialAccount,
  getSocialCandidateEmails,
  getSocialLoginErrorMessage,
  getSocialSessionToken,
  isSocialLoginSuccess,
  shouldRequireEmailVerification,
  socialLogin
} from '../data/repositories/SocialLogin';
import { completeAuthLogin } from '../utils/completeAuthLogin';
import GradientLoadingSpinner from '../presentation/components/GradientLoadingSpinner';
import type { SocialProvider } from '../data/repositories/SocialLoginMethods';

const OAUTH_CODE_PROVIDERS: readonly SocialProvider[] = ['google', 'facebook'];

function isOAuthCodeProvider(value: string): value is SocialProvider {
  return (OAUTH_CODE_PROVIDERS as readonly string[]).includes(value);
}

export default function SocialCompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const providerRaw = searchParams.get('provider') ?? 'google';
  const code = searchParams.get('code');
  const error = searchParams.get('error');

  useEffect(() => {
    if (error) {
      setErrorMessage(t('requestVerificationCodePage.socialLoginFailedMessage'));
      setIsLoading(false);
      return;
    }

    if (!code || !isOAuthCodeProvider(providerRaw)) {
      setErrorMessage(t('requestVerificationCodePage.socialLoginUnavailableMessage'));
      setIsLoading(false);
      return;
    }

    socialLogin({
      provider: providerRaw,
      ...(providerRaw === 'google' ? { authorizationCode: code } : {}),
      ...(providerRaw === 'facebook' ? { accessToken: code } : {}),
      isPromotionAgreed: false
    })
      .then((response) => {
        if (isSocialLoginSuccess(response)) {
          completeAuthLogin({ response, navigate });
          return;
        }
        if (shouldRequireEmailVerification(response)) {
          const socialSessionToken = getSocialSessionToken(response);
          const candidateEmails = getSocialCandidateEmails(response);
          if (!socialSessionToken || candidateEmails.length === 0) {
            setErrorMessage(t('requestVerificationCodePage.socialCandidateEmailsMissingMessage'));
            return;
          }
          navigate('/social-account-selection?init=true', {
            state: {
              provider: providerRaw,
              socialSessionToken,
              candidateEmails,
              continueWithNewAccountAvailable: canContinueWithNewSocialAccount(response),
              isPromotionAgreed: false
            }
          });
          return;
        }
        setErrorMessage(
          getSocialLoginErrorMessage(response) ??
            t('requestVerificationCodePage.socialLoginFailedMessage')
        );
      })
      .catch(() => {
        setErrorMessage(t('requestVerificationCodePage.socialLoginFailedMessage'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [code, error, providerRaw, navigate]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background-light dark:bg-background-dark px-6">
        <p className="text-red-500 text-center mb-4">{errorMessage}</p>
        <button
          type="button"
          onClick={() => navigate('/onboarding', { replace: true })}
          className="px-6 py-3 rounded-full bg-primary-light dark:bg-primary-dark text-white font-semibold"
        >
          {t('common.retry', '다시 시도')}
        </button>
      </div>
    );
  }

  return null;
}
